import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Save, User, Mail, Phone, Calendar, MapPin, CreditCard, FileText, Loader2, Edit, X, Plus, Building, Banknote, Shield, CheckCircle, AlertTriangle, QrCode, Video, Clock, XCircle, UserX } from 'lucide-react';
import PatientCardComponent from '../components/admin/PatientCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PatientDetails = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [formData, setFormData] = useState({});
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [subscriptionForm, setSubscriptionForm] = useState({
    plan_type: 'individual', // individual or family
    duration_years: 1, // 1, 3, or 5 years
    payment_option: 'single', // single (unica soluzione -20%) or monthly
    payment_method: 'card', // card or sepa
    card_number: '',
    card_expiry: '',
    card_cvc: '',
    iban: '',
    account_holder: ''
  });
  const [processingSubscription, setProcessingSubscription] = useState(false);
  // OTP States
  const [subscriptionStep, setSubscriptionStep] = useState('form'); // form, otp, success
  const [otpData, setOtpData] = useState(null);
  const [otpCode, setOtpCode] = useState('');
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);

  useEffect(() => {
    fetchPatientData();
  }, [patientId]);

  const fetchPatientData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Fetch patient details
      const patientRes = await axios.get(`${API}/admin/patients/${patientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const patientData = patientRes.data.user || patientRes.data;
      setPatient(patientData);
      setFormData(patientData);
      
      // Set appointments from response
      setAppointments(patientRes.data.appointments || []);

      // Fetch patient invoices
      try {
        const invoicesRes = await axios.get(`${API}/patient/${patientId}/invoices`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setInvoices(invoicesRes.data || []);
      } catch (e) {
        // Invoices endpoint might not exist
        setInvoices([]);
      }

      // Fetch subscription
      try {
        const subRes = await axios.get(`${API}/subscriptions/patient/${patientId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSubscription(subRes.data?.subscription || null);
      } catch (e) {
        setSubscription(null);
      }

    } catch (err) {
      toast.error('Errore nel caricamento dati paziente');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      await axios.put(`${API}/admin/patients/${patientId}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Profilo aggiornato!');
      setPatient(formData);
      setEditing(false);
    } catch (err) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const subscriptionPrices = {
    individual: { 1: 199, 3: 499, 5: 799 },
    family: { 1: 349, 3: 899, 5: 1399 }
  };

  // Calcola prezzo con eventuale sconto
  const calculatePrice = () => {
    const basePrice = subscriptionPrices[subscriptionForm.plan_type][subscriptionForm.duration_years];
    if (subscriptionForm.payment_option === 'single') {
      return { base: basePrice, final: Math.round(basePrice * 0.8), discount: 20 };
    }
    return { base: basePrice, final: basePrice, discount: 0 };
  };

  // Step 1: Richiedi OTP
  const handleRequestOTP = async () => {
    try {
      setSendingOtp(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.post(`${API}/subscriptions/request-otp`, {
        patient_id: patientId,
        plan_type: subscriptionForm.plan_type,
        duration_years: subscriptionForm.duration_years,
        payment_option: subscriptionForm.payment_option,
        payment_method: subscriptionForm.payment_method
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setOtpData(response.data);
      setSubscriptionStep('otp');
      toast.success(`Codice OTP inviato via ${response.data.send_method === 'sms' ? 'SMS' : 'Email'}!`);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Errore nell\'invio OTP');
    } finally {
      setSendingOtp(false);
    }
  };

  // Step 2: Verifica OTP e completa abbonamento
  const handleVerifyOTP = async () => {
    if (otpCode.length !== 6) {
      toast.error('Inserisci il codice OTP a 6 cifre');
      return;
    }
    
    try {
      setVerifyingOtp(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.post(`${API}/subscriptions/verify-otp`, {
        otp_id: otpData.otp_id,
        otp_code: otpCode
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSubscriptionStep('success');
      toast.success('Abbonamento attivato con successo!');
      
      // Dopo 3 secondi, chiudi e ricarica
      setTimeout(() => {
        setShowSubscriptionModal(false);
        resetSubscriptionForm();
        fetchPatientData();
      }, 3000);
      
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Codice OTP non valido');
    } finally {
      setVerifyingOtp(false);
    }
  };

  const resetSubscriptionForm = () => {
    setSubscriptionForm({
      plan_type: 'individual',
      duration_years: 1,
      payment_option: 'single',
      payment_method: 'card',
      card_number: '',
      card_expiry: '',
      card_cvc: '',
      iban: '',
      account_holder: ''
    });
    setSubscriptionStep('form');
    setOtpData(null);
    setOtpCode('');
  };

  // Vecchia funzione mantenuta per retrocompatibilità
  const handleSubscriptionSubmit = async () => {
    // Ora usa il flusso OTP
    await handleRequestOTP();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{background: 'linear-gradient(135deg, #e8f4fd 0%, #E8E4DB 100%)'}}>
        <Loader2 className="w-12 h-12 animate-spin text-[#2A9FCB]" />
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{background: 'linear-gradient(135deg, #e8f4fd 0%, #E8E4DB 100%)'}}>
        <div className="text-center">
          <p className="text-xl text-[#2A9FCB] mb-4">Paziente non trovato</p>
          <button onClick={() => navigate(-1)} className="text-[#2A9FCB] hover:underline">
            Torna indietro
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4" style={{background: 'linear-gradient(135deg, #e8f4fd 0%, #E8E4DB 100%)'}}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-[#2A9FCB] hover:text-[#2A9FCB] transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Torna indietro
          </button>
          
          <div className="flex items-center gap-2">
            {editing ? (
              <>
                <button
                  onClick={() => { setFormData(patient); setEditing(false); }}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-600 flex items-center gap-2"
                >
                  <X className="w-4 h-4" />
                  Annulla
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 rounded-lg bg-[#2A9FCB] text-white flex items-center gap-2 disabled:opacity-50"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Salva
                </button>
              </>
            ) : (
              <button
                onClick={() => setEditing(true)}
                className="px-4 py-2 rounded-lg bg-[#2A9FCB] text-white flex items-center gap-2"
              >
                <Edit className="w-4 h-4" />
                Modifica
              </button>
            )}
          </div>
        </div>

        {/* Patient Info Card */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
          <div className="flex items-center gap-4 mb-6 pb-6 border-b">
            <div className="w-16 h-16 rounded-full bg-[#2A9FCB] flex items-center justify-center">
              <User className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#2A9FCB]">
                {patient.first_name} {patient.last_name}
              </h1>
              <p className="text-gray-500">{patient.email}</p>
              {subscription && (
                <span className={`inline-block mt-1 px-3 py-1 rounded-full text-xs font-medium ${
                  subscription.plan_name?.toUpperCase() === 'PREMIUM' 
                    ? 'bg-purple-100 text-purple-700' 
                    : 'bg-blue-100 text-blue-700'
                }`}>
                  {subscription.plan_name || 'Base'}
                </span>
              )}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Nome */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Nome</label>
              {editing ? (
                <input
                  type="text"
                  value={formData.first_name || ''}
                  onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB]/50"
                />
              ) : (
                <p className="text-[#2A9FCB] font-medium">{patient.first_name}</p>
              )}
            </div>

            {/* Cognome */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Cognome</label>
              {editing ? (
                <input
                  type="text"
                  value={formData.last_name || ''}
                  onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB]/50"
                />
              ) : (
                <p className="text-[#2A9FCB] font-medium">{patient.last_name}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                <Mail className="w-4 h-4 inline mr-1" />
                Email
              </label>
              {editing ? (
                <input
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB]/50"
                />
              ) : (
                <p className="text-gray-700">{patient.email}</p>
              )}
            </div>

            {/* Telefono */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                <Phone className="w-4 h-4 inline mr-1" />
                Telefono
              </label>
              {editing ? (
                <input
                  type="tel"
                  value={formData.phone || ''}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB]/50"
                />
              ) : (
                <p className="text-gray-700">{patient.phone || '-'}</p>
              )}
            </div>

            {/* Data di Nascita */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                Data di Nascita
              </label>
              {editing ? (
                <input
                  type="date"
                  value={formData.birth_date || ''}
                  onChange={(e) => setFormData({...formData, birth_date: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB]/50"
                />
              ) : (
                <p className="text-gray-700">
                  {patient.birth_date ? new Date(patient.birth_date).toLocaleDateString('it-IT') : '-'}
                </p>
              )}
            </div>

            {/* Codice Fiscale */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                <CreditCard className="w-4 h-4 inline mr-1" />
                Codice Fiscale
              </label>
              {editing ? (
                <input
                  type="text"
                  value={formData.fiscal_code || ''}
                  onChange={(e) => setFormData({...formData, fiscal_code: e.target.value.toUpperCase()})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB]/50"
                />
              ) : (
                <p className="text-gray-700">{patient.fiscal_code || '-'}</p>
              )}
            </div>

            {/* Indirizzo */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-600 mb-1">
                <MapPin className="w-4 h-4 inline mr-1" />
                Indirizzo
              </label>
              {editing ? (
                <input
                  type="text"
                  value={formData.address || ''}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB]/50"
                />
              ) : (
                <p className="text-gray-700">{patient.address || '-'}</p>
              )}
            </div>

            {/* Etnia */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Etnia/Razza</label>
              {editing ? (
                <select
                  value={formData.ethnicity || ''}
                  onChange={(e) => setFormData({...formData, ethnicity: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB]/50"
                >
                  <option value="">Non specificato</option>
                  <option value="caucasica">Caucasica</option>
                  <option value="africana">Africana</option>
                  <option value="asiatica">Asiatica</option>
                  <option value="ispanica">Ispanica/Latina</option>
                  <option value="mediorientale">Mediorientale</option>
                  <option value="mista">Mista</option>
                  <option value="altro">Altro</option>
                </select>
              ) : (
                <p className="text-gray-700">{patient.ethnicity || '-'}</p>
              )}
            </div>

            {/* Sesso */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Sesso</label>
              {editing ? (
                <select
                  value={formData.gender || ''}
                  onChange={(e) => setFormData({...formData, gender: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB]/50"
                >
                  <option value="">Non specificato</option>
                  <option value="M">Maschio</option>
                  <option value="F">Femmina</option>
                </select>
              ) : (
                <p className="text-gray-700">
                  {patient.gender === 'M' ? 'Maschio' : patient.gender === 'F' ? 'Femmina' : '-'}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <button
            onClick={() => navigate(`/cartella-clinica/${patientId}`)}
            className="bg-white rounded-2xl shadow-sm p-6 text-left hover:shadow-md transition-shadow"
          >
            <FileText className="w-8 h-8 text-[#2A9FCB] mb-2" />
            <h3 className="font-bold text-[#2A9FCB]">Cartella Clinica</h3>
            <p className="text-sm text-gray-500">Visualizza e modifica la cartella clinica</p>
          </button>
          
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <CreditCard className="w-8 h-8 text-[#2A9FCB] mb-2" />
            <h3 className="font-bold text-[#2A9FCB]">Abbonamento</h3>
            {subscription ? (
              <div>
                <p className="text-sm text-gray-500">
                  Piano: <span className="font-semibold">{subscription.plan_name}</span>
                </p>
                <p className="text-sm text-gray-500">
                  Scade: {subscription.expiry_date ? new Date(subscription.expiry_date).toLocaleDateString('it-IT') : 'N/A'}
                </p>
                <p className="text-sm text-gray-500">
                  Durata: {subscription.duration_years} {subscription.duration_years === 1 ? 'anno' : 'anni'}
                </p>
                <p className="text-sm text-gray-500">
                  Importo: <span className="font-semibold text-[#2A9FCB]">€{subscription.final_price}</span>
                  {subscription.discount_applied && <span className="text-green-600 text-xs ml-1">(-20%)</span>}
                </p>
                {/* Pulsante Scarica Contratto PDF */}
                <button
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('token');
                      const response = await fetch(`${API}/subscriptions/${subscription.id}/contract-pdf`, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      if (!response.ok) throw new Error('Errore download');
                      const blob = await response.blob();
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `Contratto_${patient?.first_name}_${patient?.last_name}.pdf`;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      window.URL.revokeObjectURL(url);
                      toast.success('Contratto PDF scaricato!');
                    } catch (err) {
                      toast.error('Errore nel download del contratto');
                    }
                  }}
                  className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-green-100 text-green-700 rounded-lg text-sm font-medium hover:bg-green-200 transition-colors"
                >
                  <FileText className="w-4 h-4" />
                  Scarica Contratto PDF
                </button>
              </div>
            ) : (
              <p className="text-sm text-gray-500 mb-3">Nessun abbonamento attivo</p>
            )}
            <button
              onClick={() => setShowSubscriptionModal(true)}
              className="mt-3 px-4 py-2 rounded-lg text-white font-medium flex items-center gap-2 text-sm"
              style={{background: '#2A9FCB'}}
            >
              <Plus className="w-4 h-4" />
              {subscription ? 'Rinnova Abbonamento' : 'Registra Nuovo Abbonamento'}
            </button>
          </div>
        </div>

        {/* CARD Paziente Section */}
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <h2 className="text-lg font-bold text-[#2A9FCB] mb-4 flex items-center gap-2">
            <QrCode className="w-5 h-5" />
            Card Paziente
          </h2>
          <PatientCardComponent 
            patientId={patientId} 
            cardNumber={patient?.card_number}
            onUpdate={fetchPatientData}
          />
        </div>

        {/* Storico Appuntamenti Section */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mt-6">
          <h2 className="text-lg font-bold text-[#2A9FCB] mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Storico Appuntamenti ({appointments.length})
          </h2>
          
          {appointments.length === 0 ? (
            <p className="text-gray-500 text-center py-8">Nessun appuntamento registrato</p>
          ) : (
            <div className="space-y-3">
              {appointments
                .sort((a, b) => new Date(b.appointment_date || b.date) - new Date(a.appointment_date || a.date))
                .map((app) => {
                  // Determina lo stato e il colore
                  const isCompleted = app.status === 'completed';
                  const isNoShow = app.status === 'no_show' || app.status === 'missed';
                  const isCancelled = app.status === 'cancelled';
                  const isTelemedicine = app.is_telemedicine || app.telemedicine_link;
                  const isScheduled = app.status === 'scheduled' || app.status === 'confirmed';
                  const isCheckedIn = app.status === 'checked_in' || app.status === 'ready';
                  
                  // Colori badge
                  let statusBg, statusText, statusIcon, statusLabel;
                  if (isCompleted) {
                    statusBg = 'bg-blue-100'; statusText = 'text-blue-700';
                    statusIcon = <CheckCircle className="w-4 h-4" />;
                    statusLabel = 'Visita Effettuata';
                  } else if (isNoShow) {
                    statusBg = 'bg-red-100'; statusText = 'text-red-700';
                    statusIcon = <UserX className="w-4 h-4" />;
                    statusLabel = 'Non Presentato';
                  } else if (isCancelled) {
                    statusBg = 'bg-gray-100'; statusText = 'text-gray-600';
                    statusIcon = <XCircle className="w-4 h-4" />;
                    statusLabel = 'Cancellato';
                  } else if (isCheckedIn) {
                    statusBg = 'bg-green-100'; statusText = 'text-green-700';
                    statusIcon = <Clock className="w-4 h-4" />;
                    statusLabel = 'In Attesa / Presente';
                  } else if (isScheduled) {
                    statusBg = 'bg-yellow-100'; statusText = 'text-yellow-700';
                    statusIcon = <Clock className="w-4 h-4" />;
                    statusLabel = 'Prenotato';
                  } else {
                    statusBg = 'bg-gray-100'; statusText = 'text-gray-600';
                    statusIcon = <Calendar className="w-4 h-4" />;
                    statusLabel = app.status || 'N/D';
                  }
                  
                  const appointmentDate = new Date(app.appointment_date || app.date);
                  
                  return (
                    <div 
                      key={app.id}
                      className={`p-4 rounded-xl border-l-4 ${
                        isCompleted ? 'border-l-blue-500 bg-blue-50/50' :
                        isNoShow ? 'border-l-red-500 bg-red-50/50' :
                        isCancelled ? 'border-l-gray-400 bg-gray-50/50' :
                        isCheckedIn ? 'border-l-green-500 bg-green-50/50' :
                        'border-l-yellow-500 bg-yellow-50/50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {isTelemedicine ? (
                            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                              <Video className="w-5 h-5 text-purple-600" />
                            </div>
                          ) : (
                            <div className={`w-10 h-10 rounded-full ${statusBg} flex items-center justify-center ${statusText}`}>
                              {statusIcon}
                            </div>
                          )}
                          <div>
                            <p className="font-semibold text-gray-800">
                              {appointmentDate.toLocaleDateString('it-IT', { 
                                weekday: 'long', 
                                day: 'numeric', 
                                month: 'long', 
                                year: 'numeric' 
                              })}
                              {app.time && ` - ${app.time}`}
                            </p>
                            <p className="text-sm text-gray-500">
                              {app.reason || 'Visita oculistica'}
                              {app.doctor_name && ` • ${app.doctor_name}`}
                            </p>
                            {isTelemedicine && (
                              <span className="inline-flex items-center gap-1 text-xs text-purple-600 mt-1">
                                <Video className="w-3 h-3" /> Telemedicina
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusBg} ${statusText}`}>
                            {statusLabel}
                          </span>
                          
                          {/* Bottone Cartella Clinica per visite effettuate o no-show */}
                          {(isCompleted || isNoShow || isCheckedIn) && (
                            <button
                              onClick={() => navigate(`/cartella-clinica/${patientId}?visit=${app.id}`)}
                              className="px-3 py-1 rounded-lg text-xs font-medium bg-[#2A9FCB] text-white hover:bg-[#238bb3] transition-colors flex items-center gap-1"
                            >
                              <FileText className="w-3 h-3" />
                              Cartella
                            </button>
                          )}
                        </div>
                      </div>
                      
                      {/* Note o trascrizione per telemedicina */}
                      {app.notes && (
                        <div className="mt-3 p-3 bg-white/50 rounded-lg">
                          <p className="text-sm text-gray-600">{app.notes}</p>
                        </div>
                      )}
                      
                      {/* Trascrizione videochiamata */}
                      {isTelemedicine && app.transcription && (
                        <div className="mt-3 p-3 bg-purple-50 rounded-lg space-y-2">
                          <p className="text-xs font-semibold text-purple-700">Trascrizione Videochiamata:</p>
                          {app.transcription.patient_request && (
                            <p className="text-sm text-gray-700">
                              <span className="font-medium">Richiesta paziente:</span> {app.transcription.patient_request}
                            </p>
                          )}
                          {app.transcription.doctor_advice && (
                            <p className="text-sm text-gray-700">
                              <span className="font-medium">Consiglio medico:</span> {app.transcription.doctor_advice}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          )}
        </div>

        {/* Invoices Section */}
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <h2 className="text-lg font-bold text-[#2A9FCB] mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Fatture
          </h2>
          
          {invoices.length === 0 ? (
            <p className="text-gray-500 text-center py-8">Nessuna fattura disponibile</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b text-left text-sm text-gray-500">
                    <th className="pb-3">Data</th>
                    <th className="pb-3">Numero</th>
                    <th className="pb-3">Importo</th>
                    <th className="pb-3">Stato</th>
                    <th className="pb-3">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice) => (
                    <tr key={invoice.id} className="border-b hover:bg-gray-50">
                      <td className="py-3">{new Date(invoice.date).toLocaleDateString('it-IT')}</td>
                      <td className="py-3">{invoice.number}</td>
                      <td className="py-3 font-medium">€{invoice.amount?.toFixed(2)}</td>
                      <td className="py-3">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          invoice.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {invoice.status === 'paid' ? 'Pagata' : 'In attesa'}
                        </span>
                      </td>
                      <td className="py-3">
                        {invoice.pdf_url && (
                          <a 
                            href={invoice.pdf_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-[#2A9FCB] hover:underline text-sm"
                          >
                            Scarica PDF
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal Registra Abbonamento con OTP */}
      {showSubscriptionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-[#2A9FCB]">
                  {subscriptionStep === 'form' && 'Registra Nuovo Abbonamento'}
                  {subscriptionStep === 'otp' && 'Verifica Firma OTP'}
                  {subscriptionStep === 'success' && 'Abbonamento Attivato!'}
                </h2>
                <button 
                  onClick={() => { setShowSubscriptionModal(false); resetSubscriptionForm(); }}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-1">Per {patient?.first_name} {patient?.last_name}</p>
            </div>

            {/* Step 1: Form Abbonamento */}
            {subscriptionStep === 'form' && (
              <div className="p-6 space-y-6">
                {/* Tipo Piano */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">Tipo di Abbonamento</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setSubscriptionForm({...subscriptionForm, plan_type: 'individual'})}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        subscriptionForm.plan_type === 'individual' 
                          ? 'border-[#2A9FCB] bg-[#E6F4FA]' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <User className={`w-8 h-8 mx-auto mb-2 ${subscriptionForm.plan_type === 'individual' ? 'text-[#2A9FCB]' : 'text-gray-400'}`} />
                      <p className={`font-semibold ${subscriptionForm.plan_type === 'individual' ? 'text-[#2A9FCB]' : 'text-gray-600'}`}>Individuale</p>
                      <p className="text-xs text-gray-500 mt-1">Per una persona</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setSubscriptionForm({...subscriptionForm, plan_type: 'family'})}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        subscriptionForm.plan_type === 'family' 
                          ? 'border-[#2A9FCB] bg-[#E6F4FA]' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex justify-center mb-2">
                        <User className={`w-6 h-6 ${subscriptionForm.plan_type === 'family' ? 'text-[#2A9FCB]' : 'text-gray-400'}`} />
                        <User className={`w-6 h-6 -ml-2 ${subscriptionForm.plan_type === 'family' ? 'text-[#2A9FCB]' : 'text-gray-400'}`} />
                        <User className={`w-6 h-6 -ml-2 ${subscriptionForm.plan_type === 'family' ? 'text-[#2A9FCB]' : 'text-gray-400'}`} />
                      </div>
                      <p className={`font-semibold ${subscriptionForm.plan_type === 'family' ? 'text-[#2A9FCB]' : 'text-gray-600'}`}>Famiglia</p>
                      <p className="text-xs text-gray-500 mt-1">Per tutta la famiglia</p>
                    </button>
                  </div>
                </div>

                {/* Durata */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">Durata Abbonamento</label>
                  <div className="grid grid-cols-3 gap-3">
                    {[1, 3, 5].map(years => (
                      <button
                        key={years}
                        type="button"
                        onClick={() => setSubscriptionForm({...subscriptionForm, duration_years: years})}
                        className={`p-4 rounded-xl border-2 transition-all ${
                          subscriptionForm.duration_years === years 
                            ? 'border-[#2A9FCB] bg-[#E6F4FA]' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <p className={`text-2xl font-bold ${subscriptionForm.duration_years === years ? 'text-[#2A9FCB]' : 'text-gray-600'}`}>{years}</p>
                        <p className="text-sm text-gray-500">{years === 1 ? 'Anno' : 'Anni'}</p>
                        <p className={`text-lg font-semibold mt-2 ${subscriptionForm.duration_years === years ? 'text-[#2A9FCB]' : 'text-gray-600'}`}>
                          €{subscriptionPrices[subscriptionForm.plan_type][years]}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Opzione Pagamento */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">Modalità di Pagamento</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setSubscriptionForm({...subscriptionForm, payment_option: 'single'})}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        subscriptionForm.payment_option === 'single' 
                          ? 'border-green-500 bg-green-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <Banknote className={`w-8 h-8 mx-auto mb-2 ${subscriptionForm.payment_option === 'single' ? 'text-green-600' : 'text-gray-400'}`} />
                      <p className={`font-semibold ${subscriptionForm.payment_option === 'single' ? 'text-green-700' : 'text-gray-600'}`}>Unica Soluzione</p>
                      <p className="text-xs text-green-600 font-medium mt-1">-20% di sconto!</p>
                      <p className="text-xs text-gray-400 mt-1">Non rimborsabile</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setSubscriptionForm({...subscriptionForm, payment_option: 'monthly'})}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        subscriptionForm.payment_option === 'monthly' 
                          ? 'border-[#2A9FCB] bg-[#E6F4FA]' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <Calendar className={`w-8 h-8 mx-auto mb-2 ${subscriptionForm.payment_option === 'monthly' ? 'text-[#2A9FCB]' : 'text-gray-400'}`} />
                      <p className={`font-semibold ${subscriptionForm.payment_option === 'monthly' ? 'text-[#2A9FCB]' : 'text-gray-600'}`}>Mensile</p>
                      <p className="text-xs text-gray-500 mt-1">Prezzo pieno</p>
                      <p className="text-xs text-gray-400 mt-1">Flessibile</p>
                    </button>
                  </div>
                </div>

                {/* Metodo di Pagamento */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">Metodo di Addebito</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setSubscriptionForm({...subscriptionForm, payment_method: 'card'})}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        subscriptionForm.payment_method === 'card' 
                          ? 'border-[#2A9FCB] bg-[#E6F4FA]' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <CreditCard className={`w-8 h-8 mx-auto mb-2 ${subscriptionForm.payment_method === 'card' ? 'text-[#2A9FCB]' : 'text-gray-400'}`} />
                      <p className={`font-semibold ${subscriptionForm.payment_method === 'card' ? 'text-[#2A9FCB]' : 'text-gray-600'}`}>Carta di Credito</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setSubscriptionForm({...subscriptionForm, payment_method: 'sepa'})}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        subscriptionForm.payment_method === 'sepa' 
                          ? 'border-[#2A9FCB] bg-[#E6F4FA]' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <Building className={`w-8 h-8 mx-auto mb-2 ${subscriptionForm.payment_method === 'sepa' ? 'text-[#2A9FCB]' : 'text-gray-400'}`} />
                      <p className={`font-semibold ${subscriptionForm.payment_method === 'sepa' ? 'text-[#2A9FCB]' : 'text-gray-600'}`}>Addebito IBAN</p>
                    </button>
                  </div>
                </div>

                {/* Clausola Annullamento */}
                <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800 text-sm">Clausola di Annullamento</p>
                      <p className="text-xs text-amber-700 mt-1">
                        L'annullamento richiede un <strong>preavviso di 6 mesi</strong>. Il contratto termina 6 mesi dopo la richiesta di annullamento.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Riepilogo con calcolo sconto */}
                <div className="bg-[#E6F4FA] rounded-xl p-4">
                  <h4 className="font-semibold text-[#2A9FCB] mb-2">Riepilogo</h4>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Piano:</span>
                    <span className="font-medium">{subscriptionForm.plan_type === 'individual' ? 'Individuale' : 'Famiglia'}</span>
                  </div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Durata:</span>
                    <span className="font-medium">{subscriptionForm.duration_years} {subscriptionForm.duration_years === 1 ? 'anno' : 'anni'}</span>
                  </div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Modalità:</span>
                    <span className="font-medium">{subscriptionForm.payment_option === 'single' ? 'Unica soluzione' : 'Mensile'}</span>
                  </div>
                  {calculatePrice().discount > 0 && (
                    <>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-600">Prezzo base:</span>
                        <span className="font-medium line-through text-gray-400">€{calculatePrice().base}</span>
                      </div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-green-600">Sconto ({calculatePrice().discount}%):</span>
                        <span className="font-medium text-green-600">-€{calculatePrice().base - calculatePrice().final}</span>
                      </div>
                    </>
                  )}
                  <div className="flex justify-between text-lg font-bold mt-3 pt-3 border-t border-[#2A9FCB]/20">
                    <span className="text-[#2A9FCB]">Totale:</span>
                    <span className="text-[#2A9FCB]">€{calculatePrice().final}</span>
                  </div>
                  {subscriptionForm.payment_option === 'monthly' && (
                    <p className="text-xs text-gray-500 text-right mt-1">
                      (€{Math.round(calculatePrice().final / (subscriptionForm.duration_years * 12) * 100) / 100}/mese)
                    </p>
                  )}
                </div>

                {/* Footer Step 1 */}
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => { setShowSubscriptionModal(false); resetSubscriptionForm(); }}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Annulla
                  </button>
                  <button
                    onClick={handleRequestOTP}
                    disabled={sendingOtp}
                    className="flex-1 px-4 py-3 rounded-lg font-medium text-white flex items-center justify-center gap-2 disabled:opacity-50"
                    style={{background: '#2A9FCB'}}
                  >
                    {sendingOtp ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Invio OTP...
                      </>
                    ) : (
                      <>
                        <Shield className="w-5 h-5" />
                        Procedi alla Firma
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Verifica OTP */}
            {subscriptionStep === 'otp' && otpData && (
              <div className="p-6 space-y-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-[#E6F4FA] rounded-full flex items-center justify-center mx-auto mb-4">
                    <Shield className="w-8 h-8 text-[#2A9FCB]" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800">Verifica la tua identità</h3>
                  <p className="text-sm text-gray-500 mt-2">
                    Abbiamo inviato un codice OTP {otpData.send_method === 'sms' ? 'via SMS al numero' : 'via Email a'}{' '}
                    <strong>***{otpData.masked_contact}</strong>
                  </p>
                </div>

                {/* OTP Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-center">
                    Inserisci il codice a 6 cifre
                  </label>
                  <input
                    type="text"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    maxLength={6}
                    className="w-full text-center text-3xl tracking-[0.5em] font-mono py-4 border-2 border-gray-200 rounded-xl focus:border-[#2A9FCB] focus:ring-2 focus:ring-[#2A9FCB]/20 outline-none"
                  />
                  <p className="text-xs text-gray-500 text-center mt-2">
                    Il codice scade tra {otpData.expires_in_minutes} minuti
                  </p>
                </div>

                {/* Riepilogo Contratto */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <h4 className="font-medium text-gray-700 mb-2 text-sm">Stai firmando:</h4>
                  <div className="text-sm space-y-1">
                    <p>Piano: <strong>{otpData.contract_summary?.plan_name}</strong></p>
                    <p>Durata: <strong>{otpData.contract_summary?.duration_years} {otpData.contract_summary?.duration_years === 1 ? 'anno' : 'anni'}</strong></p>
                    <p>Totale: <strong className="text-[#2A9FCB]">€{otpData.contract_summary?.final_price}</strong>
                      {otpData.contract_summary?.discount_applied && (
                        <span className="ml-2 text-green-600 text-xs">(-{otpData.contract_summary?.discount_percentage}%)</span>
                      )}
                    </p>
                  </div>
                </div>

                {/* Clausola legale */}
                <p className="text-xs text-gray-500 text-center">
                  Inserendo il codice OTP, confermi di accettare i termini del contratto e la clausola di annullamento con preavviso di 6 mesi.
                </p>

                {/* Footer Step 2 */}
                <div className="flex gap-3">
                  <button
                    onClick={() => { setSubscriptionStep('form'); setOtpCode(''); }}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Indietro
                  </button>
                  <button
                    onClick={handleVerifyOTP}
                    disabled={verifyingOtp || otpCode.length !== 6}
                    className="flex-1 px-4 py-3 rounded-lg font-medium text-white flex items-center justify-center gap-2 disabled:opacity-50"
                    style={{background: otpCode.length === 6 ? '#10B981' : '#9CA3AF'}}
                  >
                    {verifyingOtp ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Verifica...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-5 h-5" />
                        Firma Contratto
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Successo */}
            {subscriptionStep === 'success' && (
              <div className="p-6 text-center">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-green-700 mb-2">Abbonamento Attivato!</h3>
                <p className="text-gray-600 mb-4">
                  Il contratto è stato firmato digitalmente con successo.
                </p>
                <p className="text-sm text-gray-500">
                  Una email di conferma è stata inviata al paziente.
                </p>
                <div className="mt-6">
                  <Loader2 className="w-6 h-6 animate-spin text-[#2A9FCB] mx-auto" />
                  <p className="text-xs text-gray-400 mt-2">Chiusura automatica...</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientDetails;
